/*
 * 360 spin <-> product lightbox.
 *
 * Magic360 measures its element at init time; inside a display:none modal that's a zero-size box
 * and it renders nothing. So the lightbox copy of the spin ships as `Magic360-deferred` and is
 * promoted to `Magic360` here the first time the modal actually opens, then handed to
 * `Magic360.start()` with real dimensions to work from.
 *
 * Also stops pointerdown/touchstart reaching the Blaze track, so dragging the ring in the lightbox
 * spins it instead of paging to the next slide.
 *
 * The gallery tile itself is untouched: it keeps click-and-drag spinning, and the expand button
 * rendered over it (product-gallery.liquid) is what opens the lightbox.
 *
 * Hook: [data-m360-deferred] (the lightbox slide).
 */

/** Selector for the "< 360 Spin >" controls, whose drags must reach the engine unimpeded. */
const CONTROLS = "[data-m360-prev], [data-m360-next], .m360-controls";

function startEngine(scope, attempt = 0) {
  if (typeof window.Magic360?.start !== "function") {
    // layout/theme.liquid loads the engine with `defer`; give it a moment rather than giving up.
    if (attempt > 50) return;
    window.setTimeout(() => startEngine(scope, attempt + 1), 100);
    return;
  }

  window.Magic360.start();

  // The promoted spin has its own arrows, which are bound per-element and not yet wired.
  import("@theme/magic360-arrows")
    .then((module) => module.initMagic360Arrows?.(scope))
    .catch(() => {
      /* arrows are an enhancement; drag still spins without them */
    });
}

/**
 * Promote any deferred spin inside `scope` and start it. Safe to call on every modal open —
 * once the class is swapped there is nothing left to find.
 */
function activateDeferredSpins(scope) {
  const deferred = scope.querySelectorAll(".Magic360-deferred[data-magic360-options]");
  if (!deferred.length) return;

  deferred.forEach((el) => {
    el.classList.remove("Magic360-deferred");
    el.classList.add("Magic360");
  });

  startEngine(scope);
}

/**
 * Keep a drag on the lightbox spin from also paging the Blaze slider it sits in. Magic360 binds
 * its own handler on the anchor, so by the time this fires on the way up the engine has already
 * seen the event — stopping propagation only cuts off the track above.
 */
function isolateFromSlider(root) {
  const swallow = (event) => {
    if (event.target.closest(CONTROLS)) return;
    event.stopPropagation();
  };
  root.addEventListener("pointerdown", swallow);
  root.addEventListener("touchstart", swallow, { passive: true });
}

function bindDeferredSpin(root) {
  if (root.dataset.m360LightboxBound === "true") return;
  root.dataset.m360LightboxBound = "true";

  isolateFromSlider(root);

  const modal = root.closest("[data-lightbox-modal], [data-modal]");
  if (!modal) return;

  const showClass = window.__quickfireModalController?.showClass || "active";
  const isOpen = () =>
    modal.classList.contains(showClass) || modal.getAttribute("aria-hidden") === "false";

  if (isOpen()) {
    activateDeferredSpins(modal);
    return;
  }

  // ModalController toggles the show class and aria-hidden; there is no open event to listen for.
  const observer = new MutationObserver(() => {
    if (!isOpen()) return;
    activateDeferredSpins(modal);
    observer.disconnect();
  });
  observer.observe(modal, { attributes: true, attributeFilter: ["class", "aria-hidden"] });
}

export function initMagic360Lightbox(root = document) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll("[data-m360-deferred]").forEach(bindDeferredSpin);
}

initMagic360Lightbox();
